import { useMemo } from 'react';

import { createFileRoute } from '@tanstack/react-router';

import Chart3D from '@/components/Chart3D';
import { ConfigureChartButton } from '@/components/ConfigureChartButton';
import { DeadCardButton } from '@/components/DeadCardButton';
import { DownloadButton } from '@/components/DownloadButton';
import { RangeFilterButton } from '@/components/RangeFilterButton';
import { useChartTitle } from '@/contexts/ChartContext';
import { handCombosData } from '@/data/handCombosData';
import { useChartDefaults } from '@/hooks/useChartDefaults';
import { usePageActions } from '@/hooks/usePageActions';
import { calculateComboGrid } from '@/lib/comboCalculator';
import { buildChartHeadMeta } from '@/lib/headMeta';
import { useChartStore } from '@/store/chartStore';
import type { ChartData } from '@/types/chart';

export const Route = createFileRoute('/combos')({
  component: Combos,
  head: () => buildChartHeadMeta({ chartData: handCombosData, ogImagePath: '/og/combos.png' }),
});

function Combos() {
  const deadCards = useChartStore((state) => state.deadCards);

  useChartDefaults({ colorScheme: 'TYPE_BASED_DEFAULT', maxHeight: 2.5, useLogScale: false });

  usePageActions({
    secondary: () => (
      <>
        <DeadCardButton />
        <RangeFilterButton />
        <DownloadButton />
      </>
    ),
    primary: () => <ConfigureChartButton />,
  });

  // Recalculate combos when dead cards change
  const chartData = useMemo((): ChartData => {
    if (deadCards.size === 0) {
      return handCombosData;
    }

    // Calculate new combo grid with dead cards
    const newHands = calculateComboGrid(deadCards);

    return {
      ...handCombosData,
      name: `Hand Combos (${52 - deadCards.size} cards)`,
      hands: newHands,
    };
  }, [deadCards]);

  useChartTitle(chartData);

  return <Chart3D data={chartData} />;
}
